import { Info } from "lucide-react";
import type { DutyRate, DutyStatus } from "@/types";
import { formatDate, formatPercent } from "@/lib/format";
import { computeDutyEffectiveTotalPct } from "@/services/pricing";
import { CategoryPill } from "@/components/CatalogTable";
import { colors, semanticColors, radius, spacing, heights, typography, iconSize, iconStroke } from "@/styles/tokens";

export function statusOf(rate: DutyRate): DutyStatus {
  return rate.active ? "Active" : "Superseded";
}

type ColDef = { header: string; width: number; align: "left" | "right" };
const COLS: ColDef[] = [
  { header: "HSN Code",           width: 120, align: "left"  },
  { header: "Description",        width: 280, align: "left"  },
  { header: "Category",           width: 110, align: "left"  },
  { header: "BCD %",              width:  80, align: "right" },
  { header: "SWS %",              width:  80, align: "right" },
  { header: "IGST %",             width: 160, align: "right" },
  { header: "Effective Total %",  width: 150, align: "right" },
  { header: "Valid From",         width: 110, align: "left"  },
  { header: "Status",             width: 110, align: "left"  },
];

function StatusPill({ status }: { status: DutyStatus }) {
  const scheme = {
    "Active":     { fg: colors.green600, bg: colors.green50, bd: colors.green100 },
    "Superseded": { fg: semanticColors.textMuted, bg: semanticColors.hoverBg, bd: semanticColors.border },
  }[status];
  return (
    <span
      data-testid={`duty-status-${status.toLowerCase()}`}
      style={{
        display: "inline-flex", alignItems: "center",
        height: heights.pill, padding: `0 ${spacing[8]}px`, borderRadius: radius.base,
        fontSize: typography.fontSize.xs, fontWeight: typography.fontWeight.medium,
        color: scheme.fg, backgroundColor: scheme.bg, border: `1px solid ${scheme.bd}`,
      }}
    >
      {status}
    </span>
  );
}

export function DutyRatesTable({
  rows,
  selectedId,
  onRowClick,
}: {
  rows: DutyRate[];
  selectedId?: string;
  onRowClick: (r: DutyRate) => void;
}) {
  return (
    <div
      data-testid="duty-rates-table"
      style={{
        border: `1px solid ${semanticColors.border}`, borderRadius: radius.base,
        backgroundColor: semanticColors.surface, overflow: "auto",
      }}
    >
      <div style={{ minWidth: COLS.reduce((s, c) => s + c.width, 0) }}>
        {/* Header */}
        <div style={{
          display: "flex", height: heights.control,
          backgroundColor: semanticColors.panelBg, borderBottom: `1px solid ${semanticColors.border}`,
        }}>
          {COLS.map((c) => {
            const headerCellStyle: React.CSSProperties = {
              width: c.width, minWidth: c.width, padding: `0 ${spacing[12]}px`,
              display: "flex", alignItems: "center",
              justifyContent: c.align === "right" ? "flex-end" : "flex-start",
              fontSize: typography.fontSize.sm, fontWeight: typography.fontWeight.medium, color: semanticColors.textSecondary,
              textTransform: "uppercase", letterSpacing: typography.letterSpacing.wide,
              whiteSpace: "nowrap",
            };

            if (c.header === "IGST %") {
              return (
                <div key={c.header} style={headerCellStyle}>
                  <span>{c.header}</span>
                  <span style={{
                    display: "inline-flex", alignItems: "center",
                    marginLeft: spacing[6], padding: `0 ${spacing[4]}px`,
                    fontSize: typography.fontSize.xs, fontWeight: typography.fontWeight.regular,
                    color: semanticColors.textPlaceholder, backgroundColor: semanticColors.hoverBg,
                    borderRadius: radius.sm, textTransform: "none", letterSpacing: "normal",
                  }}>
                    recoverable
                  </span>
                </div>
              );
            }

            if (c.header === "Effective Total %") {
              return (
                <div key={c.header} style={headerCellStyle}>
                  <span className="tip-target flex items-center" style={{ gap: spacing[4] }}>
                    <span>{c.header}</span>
                    <Info size={iconSize.xs} strokeWidth={iconStroke.regular} color={semanticColors.textPlaceholder} />
                    <span className="tip-bubble" style={{ whiteSpace: "normal", width: 220 }}>
                      Combined BCD + SWS as a share of goods value. IGST excluded because it is recoverable.
                    </span>
                  </span>
                </div>
              );
            }

            return <div key={c.header} style={headerCellStyle}>{c.header}</div>;
          })}
        </div>

        {/* Body */}
        <div>
          {rows.map((r) => {
            const status = statusOf(r);
            const superseded = status === "Superseded";
            const textColor = superseded ? semanticColors.textPlaceholder : semanticColors.textPrimary;
            const isSelected = r.id === selectedId;
            const effectiveTotalPct = computeDutyEffectiveTotalPct(r);
            return (
              <div
                key={r.id}
                data-testid={`duty-row-${r.id}`}
                data-status={status}
                className="material-row"
                onClick={() => onRowClick(r)}
                style={{
                  display: "flex", height: heights.row,
                  borderBottom: `1px solid ${semanticColors.border}`,
                  backgroundColor: isSelected ? semanticColors.hoverBg : semanticColors.surface,
                  cursor: "pointer",
                }}
              >
                <div style={{
                  width: COLS[0].width, minWidth: COLS[0].width, padding: `0 ${spacing[12]}px`,
                  display: "flex", alignItems: "center", fontSize: typography.fontSize.sm,
                  color: textColor, fontWeight: typography.fontWeight.medium,
                  fontVariantNumeric: "tabular-nums",
                }}>{r.hsnCode}</div>
                <div style={{ width: COLS[1].width, minWidth: COLS[1].width, padding: `0 ${spacing[12]}px`,
                              display: "flex", alignItems: "center", fontSize: typography.fontSize.sm, color: textColor,
                              whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                  {r.description}
                </div>
                <div style={{ width: COLS[2].width, minWidth: COLS[2].width, padding: `0 ${spacing[12]}px`,
                              display: "flex", alignItems: "center" }}>
                  {/* ⚠️ NULL IS A REAL STATE. anixify.duty.rate.category is FREE TEXT and
                      is never matched on, so a grouping this build does not
                      recognise arrives as null. A dash says "not grouped"; a
                      default pill would assert a category the customs schedule
                      never claimed. */}
                  {r.category ? <CategoryPill cat={r.category} /> : (
                    <span style={{ color: semanticColors.textPlaceholder }}>—</span>
                  )}
                </div>
                <div style={{ width: COLS[3].width, minWidth: COLS[3].width, padding: `0 ${spacing[12]}px`,
                              display: "flex", alignItems: "center", justifyContent: "flex-end",
                              fontSize: typography.fontSize.sm, color: textColor, fontVariantNumeric: "tabular-nums" }}>
                  {formatPercent(r.bcdPct)}
                </div>
                <div style={{ width: COLS[4].width, minWidth: COLS[4].width, padding: `0 ${spacing[12]}px`,
                              display: "flex", alignItems: "center", justifyContent: "flex-end",
                              fontSize: typography.fontSize.sm, color: textColor, fontVariantNumeric: "tabular-nums" }}>
                  {formatPercent(r.swsPct)}
                </div>
                <div style={{ width: COLS[5].width, minWidth: COLS[5].width, padding: `0 ${spacing[12]}px`,
                              display: "flex", alignItems: "center", justifyContent: "flex-end",
                              fontSize: typography.fontSize.sm, color: semanticColors.textPlaceholder, fontVariantNumeric: "tabular-nums" }}>
                  {formatPercent(r.igstPct)}
                </div>
                <div style={{ width: COLS[6].width, minWidth: COLS[6].width, padding: `0 ${spacing[12]}px`,
                              display: "flex", alignItems: "center", justifyContent: "flex-end",
                              fontSize: typography.fontSize.sm, color: textColor, fontWeight: typography.fontWeight.semibold,
                              fontVariantNumeric: "tabular-nums" }}>
                  {formatPercent(effectiveTotalPct)}
                </div>
                <div style={{ width: COLS[7].width, minWidth: COLS[7].width, padding: `0 ${spacing[12]}px`,
                              display: "flex", alignItems: "center", fontSize: typography.fontSize.sm,
                              color: textColor, fontVariantNumeric: "tabular-nums" }}>
                  {r.validFrom ? formatDate(r.validFrom) : <span style={{ color: semanticColors.textPlaceholder }}>—</span>}
                </div>
                <div style={{ width: COLS[8].width, minWidth: COLS[8].width, padding: `0 ${spacing[12]}px`,
                              display: "flex", alignItems: "center" }}>
                  <StatusPill status={status} />
                </div>
              </div>
            );
          })}
          {rows.length === 0 && (
            <div
              data-testid="duty-rates-empty"
              style={{
                padding: spacing[40], textAlign: "center",
                fontSize: typography.fontSize.base, color: semanticColors.textPlaceholder,
              }}
            >
              No duty rates match the current filters.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
